/**
 * Public Provider Profile Page
 * 
 * Displays provider details from the map.
 * Route: /provider/:id or /provider/:slug
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SEOHead } from '@/components/SEOHead';
import GlobalNav from '@/components/GlobalNav';
import Footer from '@/components/Footer';
import {
  MapPin,
  Star,
  Phone,
  Mail,
  Globe,
  Shield,
  Award,
  ArrowLeft,
  MessageSquare,
  Zap,
  Languages,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderProfilePage() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchProvider = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const res = await axios.get(`${BACKEND_URL}/api/map/provider/${id}?lang=${language}`);
        setProvider(res.data);
        
        // Track analytics
        axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
          params: {
            event_type: 'profile_open_from_map',
            provider_id: id
          }
        }).catch(() => {});
        
      } catch (err) {
        console.error('Failed to fetch provider:', err);
        setError(err.response?.data?.detail || 'Provider not found');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchProvider();
    }
  }, [id, language]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <GlobalNav />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-48 w-full mb-6" />
          <Skeleton className="h-24 w-full mb-4" />
          <Skeleton className="h-24 w-full" />
        </main>
        <Footer />
      </div>
    );
  }
  
  if (error || !provider) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <GlobalNav />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <Button
            variant="ghost"
            onClick={() => navigate('/map')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('map.pageTitle') || 'Back to Map'}
          </Button>
          
          <Card className="text-center py-12">
            <CardContent>
              <h2 className="text-2xl font-semibold mb-4">
                {t('errors.providerNotFound') || 'Provider Not Found'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {error || t('errors.providerNotFoundDesc') || 'The provider you are looking for does not exist or has been removed.'}
              </p>
              <Button asChild>
                <Link to="/map">
                  {t('map.pageTitle') || 'Browse Map'}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }
  
  const tierColors = {
    premium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    boost: 'bg-green-100 text-green-800 border-green-300',
    basic: 'bg-blue-100 text-blue-800 border-blue-300'
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead 
        title={`${provider.name} - ${t('app.name') || 'Keikka'}`}
        description={provider.description || `Service provider in ${provider.city}, ${provider.country}`}
      />
      
      <GlobalNav />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/map" className="hover:text-foreground transition-colors">
            {t('map.pageTitle') || 'Map'}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground">{provider.name}</span>
        </nav>
        
        {/* Header Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-2xl md:text-3xl font-bold">{provider.name}</h1>
                  {provider.is_verified && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      <Shield className="w-3 h-3 mr-1" />
                      {t('map.verified') || 'Verified'}
                    </Badge>
                  )}
                  {provider.is_featured && (
                    <Badge className={tierColors[provider.map_tier] || tierColors.basic}>
                      <Zap className="w-3 h-3 mr-1" />
                      {provider.map_tier === 'premium' ? 'Premium' : 'Boost'}
                    </Badge>
                  )}
                </div>
                
                {provider.company_name && provider.company_name !== provider.name && (
                  <p className="text-lg text-muted-foreground mb-2">{provider.company_name}</p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {provider.city}, {provider.country}
                  </span>
                  {provider.rating && (
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {provider.rating.toFixed(1)}
                      {provider.review_count && (
                        <span>({provider.review_count} {t('reviews') || 'reviews'})</span>
                      )}
                    </span>
                  )}
                </div>
                
                {/* Categories */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {provider.categories?.map((cat, i) => (
                    <Badge key={i} variant="outline">
                      {t(`categories.${cat}`) || cat}
                    </Badge>
                  ))}
                </div>
                
                {/* Trust Score */}
                {provider.trust_score && (
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    <span className="font-medium">
                      {t('trustScore') || 'Trust Score'}: {provider.trust_score}%
                    </span>
                    {provider.trust_level && (
                      <Badge variant="secondary">{provider.trust_level}</Badge>
                    )}
                  </div>
                )}
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 min-w-[200px]">
                {provider.show_contact && provider.contact?.phone && (
                  <Button size="lg" className="gap-2" asChild>
                    <a href={`tel:${provider.contact.phone}`}>
                      <Phone className="w-4 h-4" />
                      {provider.contact.phone}
                    </a>
                  </Button>
                )}
                
                {provider.show_contact && provider.contact?.email && (
                  <Button variant="outline" size="lg" className="gap-2" asChild>
                    <a href={`mailto:${provider.contact.email}`}>
                      <Mail className="w-4 h-4" />
                      {t('map.contactProvider') || 'Send Email'}
                    </a>
                  </Button>
                )}
                
                {provider.show_contact && provider.contact?.website && (
                  <Button variant="outline" size="lg" className="gap-2" asChild>
                    <a href={provider.contact.website} target="_blank" rel="noopener noreferrer">
                      <Globe className="w-4 h-4" />
                      {t('website') || 'Website'}
                    </a>
                  </Button>
                )}
                
                {!provider.show_contact && (
                  <Button size="lg" className="gap-2">
                    <MessageSquare className="w-4 h-4" />
                    {t('map.requestService') || 'Request Service'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Description */}
        {provider.description && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('about') || 'About'}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{provider.description}</p>
            </CardContent>
          </Card>
        )}
        
        {/* Languages */}
        {provider.languages && provider.languages.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="w-5 h-5" />
                {t('languages') || 'Languages'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {provider.languages.map((lang, i) => (
                  <Badge key={i} variant="secondary">
                    {t(`languageNames.${lang}`) || lang.toUpperCase()}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Back to Map */}
        <div className="text-center mt-8">
          <Button variant="outline" asChild>
            <Link to="/map">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('map.pageTitle') || 'Back to Map'}
            </Link>
          </Button>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
